import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { DashboardWindow } from "./dashboardHelpers";
import {
  BareRange,
  BuilderMode,
  DateRange,
  DateRangeSelection,
  dateRangeTriggerText,
  dateToInputValue,
  formatDay,
  formatRange,
  getToday,
  FULL_PERIOD_LABEL,
  INCLUDE_CURRENT_LABEL,
  isOpenEnd,
  isOpenStart,
  isSameDay,
  LAST_UNIT_LABEL,
  LAST_UNITS,
  PANEL_WIDTH,
  SelectionUnit,
  THIS_UNIT_LABEL,
  THIS_UNITS,
  ThisUnit,
  WEEKDAYS,
} from "./dateRangeSelection";
import { useDateRangePicker } from "./useDateRangePicker";
import { useIsMobile } from "./useIsMobile";
import { useSwipeDownToClose } from "./useSwipeDownToClose";

/** Outline calendar trigger icon (design date.svg): rounded body, binding pins, header rule. */
const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={className}
    aria-hidden="true"
  >
    <rect
      x="1.75"
      y="2.75"
      width="12.5"
      height="11.5"
      rx="2.25"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path d="M1.75 6.25h12.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M5 1.25v3M11 1.25v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// ---------------------------------------------------------------------------
// Calendar grid
// ---------------------------------------------------------------------------

interface GridProps {
  displayRange: BareRange;
  bounds: BareRange | null;
  today: Date;
  /** "All time" is selected: paint no endpoints/in-range so the calendar reads as unset. */
  allTime?: boolean;
  onPick: (date: Date) => void;
  onHover: (date: Date | null) => void;
}

const cellClass = (opts: {
  isEndpoint: boolean;
  inRange: boolean;
  isCurrent: boolean;
  isDisabled: boolean;
}): string => {
  const cls = [
    "w-8 h-8 flex items-center justify-center text-sm select-none transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-button-background focus-visible:z-10",
  ];
  if (opts.isDisabled) {
    cls.push("text-main-foreground/25 cursor-default");
  } else {
    cls.push("cursor-pointer");
  }
  if (opts.isEndpoint) {
    cls.push("bg-primary-button-background text-primary-button-foreground rounded-md font-medium");
  } else if (opts.inRange) {
    cls.push("bg-primary-button-background/15");
  } else if (!opts.isDisabled) {
    cls.push("rounded-md hover:bg-main-text-button-hover-background");
  }
  if (opts.isCurrent && !opts.isEndpoint) {
    cls.push("font-bold text-primary-button-background");
  }
  return cls.join(" ");
};

const MonthGrid: React.FC<GridProps & { year: number; month: number }> = ({
  year,
  month,
  displayRange,
  bounds,
  today,
  allTime,
  onPick,
  onHover,
}) => {
  const firstDay = new Date(year, month, 1);
  const leadingBlanks = (firstDay.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startT = displayRange.start.getTime();
  const endT = displayRange.end.getTime();

  const cells: React.ReactNode[] = [];
  for (let i = 0; i < leadingBlanks; i++) {
    cells.push(<div key={`blank-${i}`} className="w-8 h-8" />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const t = date.getTime();
    const isDisabled = bounds ? t < bounds.start.getTime() || t > bounds.end.getTime() : false;
    const isEndpoint =
      !allTime && (isSameDay(date, displayRange.start) || isSameDay(date, displayRange.end));
    const inRange = !allTime && t > startT && t < endT;
    const isToday = isSameDay(date, today);
    cells.push(
      <button
        key={day}
        type="button"
        disabled={isDisabled}
        onClick={() => onPick(date)}
        onMouseEnter={() => onHover(date)}
        aria-label={formatDay(date)}
        aria-pressed={isEndpoint || inRange}
        aria-current={isToday ? "date" : undefined}
        className={cellClass({ isEndpoint, inRange, isCurrent: isToday, isDisabled })}
      >
        {day}
      </button>
    );
  }

  return (
    <div className="grid grid-cols-7" onMouseLeave={() => onHover(null)}>
      {WEEKDAYS.map((wd, i) => (
        <div
          key={`wd-${i}`}
          aria-hidden="true"
          className="w-8 h-8 flex items-center justify-center text-xs text-main-muted-foreground"
        >
          {wd}
        </div>
      ))}
      {cells}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Builder inputs
// ---------------------------------------------------------------------------

const CountInput: React.FC<{
  value: string;
  onChange: (text: string) => void;
  ariaLabel: string;
}> = ({ value, onChange, ariaLabel }) => (
  <input
    type="text"
    inputMode="numeric"
    value={value}
    aria-label={ariaLabel}
    onChange={e => onChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
    className="w-12 px-1 py-1 text-sm text-center bg-main-background border border-main-border rounded-lg text-main-foreground focus:outline-none focus:ring-1 focus:ring-primary-button-background"
  />
);

/**
 * The chevron-suffixed <select> used for the mode / unit / period dropdowns.
 *
 * `field-sizing-content` shrinks each control to the option actually selected. Without it a
 * <select> reserves width for its WIDEST option, so the mode dropdown was permanently as wide as
 * "Between" and the unit dropdown as wide as "quarters". `pr-8` stays so the overlaid chevron
 * keeps its gutter. Where `field-sizing` is unsupported the declaration is ignored and the control
 * falls back to widest-option sizing, which is the previous behaviour.
 */
const SelectControl: React.FC<{
  value: string;
  ariaLabel: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}> = ({ value, ariaLabel, onChange, children }) => (
  <span className="relative inline-flex items-center">
    <select
      value={value}
      aria-label={ariaLabel}
      onChange={e => onChange(e.target.value)}
      className="appearance-none field-sizing-content pl-3 pr-8 py-1 text-sm font-medium bg-main-background border border-main-border rounded-lg text-main-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-button-background"
    >
      {children}
    </select>
    <span className="material-icons !text-base absolute right-2 pointer-events-none text-main-muted-foreground">
      expand_more
    </span>
  </span>
);

// ---------------------------------------------------------------------------
// DateRangeSlicer
// ---------------------------------------------------------------------------

interface DateRangeSlicerProps {
  id: string;
  name: string;
  label: string;
  /**
   * Deprecated spec field, accepted for compatibility but ignored: the picker always
   * renders day-level granularity so every date filter behaves consistently.
   */
  granularity?: "day" | "month" | "year" | null;
  defaultDateRange: DateRange;
  /**
   * Deprecated: the data's all-time date range no longer bounds the calendar (any date is
   * selectable) and "All time" is the open sentinel window. Accepted for compatibility with
   * older callers but not required.
   */
  allTimeDateRange?: DateRange | null;
  /**
   * The dashboard's configured structured default. Passed through so the picker can seed its
   * builder from the selection directly rather than reverse-mapping the resolved dates.
   */
  defaultSelection?: DateRangeSelection | null;
  initialDateRange: DateRange;
  /** `selection` is the structured intent behind the dates — see useDateRangePicker. */
  onChange: (value: DateRange, selection: DateRangeSelection | null) => void;
  targetWindow: DashboardWindow;
  /**
   * Which mobile (<640px) presentation to use. The date filter appears in two places on phones:
   * `"pill"` is the compact chip on the dashboard's filter row, `"row"` is the full-width card
   * inside the Filters sheet. Ignored on desktop, which always renders the `themed-slicer-input`
   * trigger.
   */
  mobileVariant?: "pill" | "row";
}

export const DateRangeSlicer: React.FC<DateRangeSlicerProps> = ({
  id,
  label,
  allTimeDateRange,
  defaultSelection,
  initialDateRange,
  onChange,
  targetWindow,
  mobileVariant = "row",
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);

  const picker = useDateRangePicker({
    label,
    initialDateRange,
    allTimeDateRange,
    isOpen,
    defaultSelection,
    onChange,
  });

  // ----- popover open/close -----

  const openPopover = () => {
    if (buttonRef.current) {
      setButtonRect(buttonRef.current.getBoundingClientRect());
    }
    picker.seed();
    setIsOpen(true);
  };

  const closePopover = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Move focus into the dialog when it opens.
  useEffect(() => {
    if (isOpen) {
      popoverRef.current?.focus({ preventScroll: true });
    }
  }, [isOpen]);

  // Keep Tab cycling within the dialog while it is open.
  const handlePopoverKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !popoverRef.current) {
      return;
    }
    const focusables = popoverRef.current.querySelectorAll<HTMLElement>(
      "button:not(:disabled), input, select"
    );
    if (focusables.length === 0) {
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = targetWindow.document.activeElement;
    if (e.shiftKey && (active === first || active === popoverRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Close on outside click, Escape, scroll, or resize (fixed position is a snapshot of the
  // trigger rect, so it goes stale when the page moves).
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || buttonRef.current?.contains(target)) {
        return;
      }
      closePopover();
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };
    const handleScroll = (e: Event) => {
      if (popoverRef.current?.contains(e.target as Node)) {
        return;
      }
      closePopover();
    };
    targetWindow.document.addEventListener("mousedown", handleClickOutside);
    targetWindow.document.addEventListener("keydown", handleEscape);
    targetWindow.addEventListener("scroll", handleScroll, true);
    targetWindow.addEventListener("resize", closePopover);
    return () => {
      targetWindow.document.removeEventListener("mousedown", handleClickOutside);
      targetWindow.document.removeEventListener("keydown", handleEscape);
      targetWindow.removeEventListener("scroll", handleScroll, true);
      targetWindow.removeEventListener("resize", closePopover);
    };
  }, [isOpen, targetWindow, closePopover]);

  // ----- render -----

  const today = getToday();

  const gridProps: GridProps = {
    displayRange: picker.displayRange,
    bounds: picker.calendarBounds,
    today,
    allTime: picker.isAllTime,
    onPick: picker.onPick,
    onHover: picker.onHover,
  };

  const focusRingClass =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-button-background";

  const navButtonClass =
    "w-7 h-7 flex items-center justify-center rounded-md cursor-pointer text-main-muted-foreground " +
    "hover:bg-main-text-button-hover-background hover:text-main-foreground disabled:opacity-30 " +
    `disabled:cursor-default disabled:hover:bg-transparent ${focusRingClass}`;

  // No truncate/nowrap: the column is sized (see the aside below) so every real preset fits on
  // one line, and anything that does not — only the generated "current selection" row can be, e.g.
  // "Last 9999 complete quarters" — wraps instead of being clipped, so no text is ever hidden.
  const presetRowBaseClass =
    "text-left px-2 py-1.5 text-sm rounded-md cursor-pointer transition-colors";
  const activePresetClass =
    "bg-primary-button-background/15 text-primary-button-background font-semibold";
  const inactivePresetClass = "hover:bg-main-text-button-hover-background";

  const popoverTop = buttonRect ? buttonRect.bottom + 4 : 0;
  const popoverLeft = buttonRect
    ? Math.max(8, Math.min(buttonRect.left, targetWindow.innerWidth - PANEL_WIDTH - 8))
    : 0;
  const popoverMaxHeight = Math.max(240, targetWindow.innerHeight - popoverTop - 8);

  // On phones the desktop popover (anchored below the trigger, PANEL_WIDTH wide) is cut off at the
  // bottom, so render the panel as a full-width bottom sheet that expands upward and scrolls. The
  // inner layout keeps presets on the left but stacks the two months vertically (see max-sm: below).
  // Via the hook rather than a bare `innerWidth` read so the trigger and panel re-render on rotate.
  const isMobile = useIsMobile(targetWindow);
  const popoverStyle: React.CSSProperties = isMobile
    ? {
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        maxHeight: "90vh",
        zIndex: 9999,
      }
    : {
        position: "fixed",
        top: popoverTop,
        left: popoverLeft,
        width: PANEL_WIDTH,
        maxHeight: popoverMaxHeight,
        zIndex: 9999,
      };

  // On mobile the panel is a bottom sheet, so it gets the same swipe-down-to-close as the other
  // dashboard drawers. Dismissing this way matches the outside-tap close (closePopover, no apply,
  // no focus restore). The panel root is its own overflow-y-auto scroller; the hook's scroll gate
  // checks it inclusively, so a scrolled-down panel scrolls back up instead of dismissing.
  useSwipeDownToClose({
    sheetRef: popoverRef,
    enabled: isMobile && isOpen,
    onDismiss: closePopover,
  });

  const popover =
    isOpen && buttonRect ? (
      <div
        ref={popoverRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${label} date range`}
        tabIndex={-1}
        onKeyDown={handlePopoverKeyDown}
        style={popoverStyle}
        className="flex flex-col overflow-y-auto max-sm:overscroll-y-none themed-menu-scrollbar rounded-lg max-sm:rounded-b-none max-sm:rounded-t-2xl bg-main-background text-main-foreground shadow-[0_12px_48px_rgba(0,0,0,0.2)] outline-none"
      >
        {/* Announce the working selection to screen readers */}
        <span role="status" className="sr-only">
          {formatRange(picker.range)}
        </span>

        {/* Relative range builder. A full-width strip across the top of the panel, above the
            presets/calendar split, with its controls left-aligned. It sits outside the split so
            it spans the sidebar too, and `border-b` now divides the whole header strip from the
            body rather than just the calendar column. */}
        <div className="flex items-center gap-2 flex-wrap px-4 py-3 border-b border-main-border">
          <SelectControl
            value={picker.mode}
            ariaLabel="Date range mode"
            onChange={value => picker.onModeChange(value as BuilderMode)}
          >
            <option value="last">Last</option>
            <option value="this">This</option>
            <option value="between">Between</option>
          </SelectControl>

          {picker.mode === "last" && (
            <>
              <CountInput
                value={picker.lastNText}
                ariaLabel="Number of periods"
                onChange={picker.onLastNTextChange}
              />
              <SelectControl
                value={picker.lastUnit}
                ariaLabel="Unit"
                onChange={value => picker.onLastUnitChange(value as SelectionUnit)}
              >
                {LAST_UNITS.map(unit => (
                  <option key={unit} value={unit}>
                    {LAST_UNIT_LABEL[unit]}
                  </option>
                ))}
              </SelectControl>
              <label className="flex items-center gap-1.5 text-sm text-main-foreground/80 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={picker.includeCurrent}
                  aria-label={INCLUDE_CURRENT_LABEL[picker.lastUnit]}
                  onChange={e => picker.onIncludeCurrentChange(e.target.checked)}
                  className="accent-primary-button-background cursor-pointer"
                />
                {INCLUDE_CURRENT_LABEL[picker.lastUnit]}
              </label>
            </>
          )}

          {picker.mode === "this" && (
            <>
              <SelectControl
                value={picker.thisUnit}
                ariaLabel="Period"
                onChange={value => picker.onThisUnitChange(value as ThisUnit)}
              >
                {THIS_UNITS.map(unit => (
                  <option key={unit} value={unit}>
                    {THIS_UNIT_LABEL[unit]}
                  </option>
                ))}
              </SelectControl>
              <label className="flex items-center gap-1.5 text-sm text-main-foreground/80 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={picker.thisFullPeriod}
                  aria-label={FULL_PERIOD_LABEL[picker.thisUnit]}
                  onChange={e => picker.onThisFullPeriodChange(e.target.checked)}
                  className="accent-primary-button-background cursor-pointer"
                />
                {FULL_PERIOD_LABEL[picker.thisUnit]}
              </label>
            </>
          )}

          {picker.mode === "between" && (
            <span className="flex items-center gap-2 max-sm:w-full">
              <input
                type="date"
                value={
                  picker.isAllTime || isOpenStart(picker.displayRange.start)
                    ? ""
                    : dateToInputValue(picker.displayRange.start)
                }
                aria-label="Start date"
                onChange={e => picker.onBetweenStartInput(e.target.value)}
                className="w-40 max-sm:w-0 max-sm:flex-1 max-sm:min-w-0 text-sm px-2 py-1 border border-main-border rounded-lg bg-main-background text-main-foreground focus:outline-none focus:ring-1 focus:ring-primary-button-background"
              />
              <span className="text-main-muted-foreground">–</span>
              <input
                type="date"
                value={
                  picker.isAllTime || isOpenEnd(picker.displayRange.end)
                    ? ""
                    : dateToInputValue(picker.displayRange.end)
                }
                aria-label="End date"
                onChange={e => picker.onBetweenEndInput(e.target.value)}
                className="w-40 max-sm:w-0 max-sm:flex-1 max-sm:min-w-0 text-sm px-2 py-1 border border-main-border rounded-lg bg-main-background text-main-foreground focus:outline-none focus:ring-1 focus:ring-primary-button-background"
              />
            </span>
          )}
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Presets sidebar */}
          {/* max-sm:w-[136px]: wide enough that the longest preset ("Last 12 months") is one
              line. Exact px because the width is derived from a px text measurement — the viewer
              iframe pins its root to 14px, so text-sm is 12.25px and the rem scale would not
              track it. Sized against the calendar's floor: a month grid is 7 x w-8 = 196px plus
              the section's max-sm:px-2, so on a 375px phone the aside cannot exceed ~164px. */}
          <aside className="w-36 max-sm:w-[136px] shrink-0 border-r border-main-border p-2 flex flex-col gap-0.5 overflow-y-auto themed-menu-scrollbar">
            <div className="px-2 py-1.5 text-xs font-semibold text-main-muted-foreground">
              Presets
            </div>
            {picker.presets.map(preset => (
              <button
                key={preset.id}
                type="button"
                onClick={() => picker.onPresetClick(preset)}
                aria-pressed={preset.id === picker.activePresetId}
                className={`${presetRowBaseClass} ${focusRingClass} ${
                  preset.id === picker.activePresetId ? activePresetClass : inactivePresetClass
                }`}
              >
                {preset.label}
              </button>
            ))}
            {/* A relative selection with no matching preset ("Last 5 months") is still shown
                reflected in the list. Same active styling as a real preset, but not clickable
                since it's not one. Manual calendar ranges highlight the "Custom" preset row
                above instead. */}
            {picker.activePresetId === null && (
              <div aria-current="true" className={`${presetRowBaseClass} ${activePresetClass}`}>
                {picker.describeSelection()}
              </div>
            )}
          </aside>

          <section className="flex-1 min-w-0 flex flex-col">
            {/* Calendar panels (nav chevrons overlay the title line) */}
            <div className="relative px-4 max-sm:px-2 pt-3 pb-4">
              <button
                type="button"
                aria-label="Previous month"
                disabled={!picker.canGoPrev}
                onClick={picker.goPrev}
                className={`absolute left-3 top-3 ${navButtonClass}`}
              >
                <span className="material-icons !text-xl">chevron_left</span>
              </button>
              <button
                type="button"
                aria-label="Next month"
                disabled={!picker.canGoNext}
                onClick={picker.goNext}
                className={`absolute right-3 top-3 ${navButtonClass}`}
              >
                <span className="material-icons !text-xl">chevron_right</span>
              </button>
              <div className="flex gap-6 max-sm:flex-col max-sm:items-center max-sm:gap-4">
                {[0, 1].map(offset => {
                  const idx = picker.viewIndex + offset;
                  const year = Math.floor(idx / 12);
                  return (
                    <div key={offset} className="flex-1 flex flex-col items-center gap-2">
                      <div className="h-7 flex items-center text-sm font-semibold">
                        {picker.panelTitles[offset]}
                      </div>
                      <MonthGrid year={year} month={idx - year * 12} {...gridProps} />
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </div>
      </div>
    ) : null;

  const triggerText = dateRangeTriggerText(label, picker.chipText);

  // Mobile (<640px), dashboard filter row: a compact chip next to the "All Filters" button, per the
  // Figma "Date + More Filters" frame. Card surface + shadow (no border) so it reads as a control
  // floating over the page, like the desktop `themed-slicer-input` it replaces here.
  if (isMobile && mobileVariant === "pill") {
    return (
      <div className="relative inline-flex min-w-0 items-center">
        <button
          id={id}
          ref={buttonRef}
          type="button"
          onClick={() => (isOpen ? closePopover() : openPopover())}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-label={triggerText}
          className="flex h-[32px] min-w-0 items-center gap-[8px] rounded-[8px] bg-card-background px-[12px] text-card-foreground shadow-card cursor-pointer"
        >
          <span className="material-icons !text-[20px] shrink-0">calendar_today</span>
          <span className="truncate text-[12px] leading-[18px] font-medium">{triggerText}</span>
          <span className="material-icons !text-[16px] shrink-0">expand_more</span>
        </button>
        {popover && createPortal(popover, targetWindow.document.body)}
      </div>
    );
  }

  // Mobile, inside the Filters sheet: a collapsed section row styled to match MultiSelectSlicer's
  // collapsed card — same surface, radius, padding and right-aligned muted value — so the sheet
  // reads as one stack of filter rows (see the Figma "Filters Closed" frame).
  if (isMobile) {
    return (
      <div className="w-full rounded-[8px] bg-main-background-secondary px-[12px] py-[12px]">
        <button
          id={id}
          ref={buttonRef}
          type="button"
          onClick={() => (isOpen ? closePopover() : openPopover())}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-label={triggerText}
          className="flex w-full items-center gap-[12px] text-left cursor-pointer"
        >
          <span className="material-icons !text-[20px] shrink-0 text-main-foreground">
            calendar_today
          </span>
          <span className="flex-1 min-w-0 truncate text-[14px] leading-[18px] font-bold text-main-foreground">
            {label}
          </span>
          <span className="flex-1 min-w-0 truncate text-right text-[14px] leading-[18px] font-semibold text-main-muted-foreground">
            {picker.chipText}
          </span>
          <span className="material-icons !text-[16px] shrink-0 text-main-foreground">
            expand_more
          </span>
        </button>
        {popover && createPortal(popover, targetWindow.document.body)}
      </div>
    );
  }

  return (
    <div className="relative inline-flex items-center">
      <button
        id={id}
        ref={buttonRef}
        type="button"
        onClick={() => (isOpen ? closePopover() : openPopover())}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={triggerText}
        className="themed-slicer-input text-left"
      >
        <CalendarIcon className="shrink-0 text-card-foreground/70" />
        <span className="truncate">{triggerText}</span>
      </button>
      <span className="absolute right-3 flex items-center text-card-foreground/70 pointer-events-none">
        <span className="material-icons !text-lg">expand_more</span>
      </span>
      {popover && createPortal(popover, targetWindow.document.body)}
    </div>
  );
};
